import { WarrantyStatusStats } from './../models/warranty-stats.model';
import { QuoteType } from './../models/quote.model';
import { Authorization } from './../models/authorization.model';
import { ShortenedWarranty } from './../models/shortened-warranty.model';
import { ID } from '../models/ID.model';
import { Installment } from './../models/installment.model';
import { AuthService } from './auth.service';
import { PaymentInformation } from './../models/payment-information.model';
import { Warranty } from './../models/warranty.model';
import { SortConfiguration } from './../components/generics/data-table/data-table.component';
import { HttpClient } from '@angular/common/http';
import { UtilsService } from './utils.service';
import { Injectable } from '@angular/core';
import { WarrantyContract, ContractModel, ContractType } from '../models/contract.model';
import { PersonType } from '../models/person-type.model';
import { Service } from '../models/service.model';
import { Sex } from '../models/sex.model';

export interface NewWarrantyParams {
    idCotizacion: ID;
    inquilinos: TenantInitializationParams[];
}

export interface TenantInitializationParams {
    tipoPersona: PersonType;
    dni?: string;
    cuit?: string;
    nombre?: string;
    apellido?: string;
    celular: string;
    sexo?: string;
    email?: string;
    razonSocial?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GuarantiesService {
    signatories: any[] = [];

    constructor(
        private http: HttpClient,
        private utilsService: UtilsService,
        private authService: AuthService
    ) { 
        this.loadSignatories();
    }

    getGuarantees(page: number, itemsPerPage: number, sorts: SortConfiguration[], extraParams: any) {
        const url = '/garantias/resumenes';
        const headers = {
            'page-number': String(page),
            'page-size': String(itemsPerPage)
        };
        const params: any = {
            'sort-by': this.utilsService.generateSortQueryParam(sorts)
        };
        const user = this.authService.getUser();
        if (user?.tipo === 'AGENTE') {
            params['id-agente'] = user.agente?.id;
        } else if (user?.tipo === 'OFICINA') {
            params['id-oficina'] = user.oficina?.id;
        }
        Object.assign(params, JSON.parse(JSON.stringify(extraParams)));
        return this.http.get<ShortenedWarranty[]>(url, { headers, observe: 'response', params });
    }

    newWarranty(params: NewWarrantyParams) {
        return this.http.post<Warranty>(`/garantias`, params);
    }

    deleteWarranty(warrantyId: ID) {
        return this.http.delete(`/garantias/${warrantyId}`);
    }

    getWarrantyById(id: ID) {
        return this.http.get<Warranty>(`/garantias/${id}`);
    }

    associateProperty(warrantyId: ID, propertyId: ID) {
        return this.http.put<Warranty>(`/garantias/${warrantyId}/propiedad/${propertyId}`, {});
    }

    assignAgent(warrantyId: ID, brokerId: ID) {
        return this.http.put<Warranty>(`/garantias/${warrantyId}/agente/${brokerId}`, {});
    }

    requestPayment(warrantyId: ID) {
        return this.http.post<Warranty>(`/garantias/${warrantyId}/pagos/solicitud`, {});
    }

    getPaymentInformation(warrantyHash: string) {
        return this.http.get<PaymentInformation>(`/garantias/${warrantyHash}/pagos/informacion`);
    }

    getAvailableContractModels(warrantyId: ID) {
        return this.http.get<ContractModel[]>(`/garantias/${warrantyId}/contratos/modelos`);
    }

    downloadContractPreview(warrantyId: ID, contract: WarrantyContract, contractType: ContractType) {
        return this.http.post(`/garantias/${ warrantyId }/contratos/vista-previa`, contract, { responseType: 'arraybuffer', observe: 'response', params: { 'tipo-contrato': contractType } });
    }

    generateContract(warrantyId: ID, contract: WarrantyContract) {
        return this.http.post<Warranty>(`/garantias/${ warrantyId }/contratos/confirmacion`, contract);
    }

    reportAuthorization(authorizationId: ID, dictum: 'aprobacion' | 'rechazo', code: string, motivo: string) {
        return this.http.post<Authorization>(`/autorizaciones/${authorizationId}/${dictum}?codigo=${code}`, { motivo });
    }

    resendPaymentForm(id: ID) {
        return this.http.post(`/garantias/${id}/pagos/solicitud/reenvio`, {});
    }

    reportSignature(warrantyId: ID) {
        return this.http.post<Warranty>(`/garantias/${warrantyId}/contratos/firma`, {});
    }

    getInstallments(warrantyId: ID) {
        return this.http.get<Installment[]>(`/alquileres/${warrantyId}/cuotas`);
    }

    resendAuthorizationEmail(authorizationId: ID) {
        return this.http.post(`/autorizaciones/${authorizationId}/reenvio`, {});
    }

    requotation(id: ID, requotationParams: { tipo: QuoteType, alquiler: number, expensas: number, actualizacion?: number, periodoActualizacion?: number, duracion: number, servicios: Service[] }) {
        return this.http.put<Warranty>(`/garantias/${id}/cotizaciones`, requotationParams);
    }

    rescoring(id: ID, rescoringParams: { idInquilino: ID, dni?: string, sexo?: Sex, cuit?: string }[]) {
        return this.http.put<Warranty>(`/garantias/${id}/informes`, rescoringParams);
    }

    getStatusStats() {
        const params: any = {};
        const user = this.authService.getUser();
        if (user?.tipo === 'AGENTE') {
            params['id-agente'] = user.agente?.id;
        } else if (user?.tipo === 'OFICINA') {
            params['id-oficina'] = user.oficina?.id;
        }
        return this.http.get<WarrantyStatusStats>(`/garantias/estadisticas/estados`, { params });
    }

    loadSignatories(): void {
    this.http.get<any[]>('assets/signatories/signatories.json')
      .subscribe({
        next: (data) => this.signatories = data,
        error: (err) => console.error('Error al cargar firmantes:', err)
      });
  }
  getSignatories(): any[] {
    return this.signatories;
  }

}
